import { useEffect, useState } from "react";
import { FileText, Download } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money, fmtDate, API } from "@/lib/api";

/** Monthly statement of fees received, for the arbitrator's accountant. */
export const PayoutStatements = () => {
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState("");
  const [stmt, setStmt] = useState(null);

  useEffect(() => {
    api.get("/dashboard/payouts/statements").then(({ data }) => {
      setMonths(data.months);
      if (data.months[0]) setMonth(data.months[0].month);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!month) return;
    setStmt(null);
    api.get("/dashboard/payouts/statement", { params: { month } })
      .then(({ data }) => setStmt(data)).catch((e) => toast.error(errMsg(e)));
  }, [month]);

  return (
    <section className="border border-border bg-card p-7" data-testid="payout-statements">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 label-caps"><FileText className="h-3.5 w-3.5" /> Monthly fee statements</p>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed max-w-2xl">
            A statement of the fees you received in each month, ready for your accountant. Where a
            tribunal has more than one member, your equal share of each fee note is shown.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="statement-month-select"
            className="h-10 px-3 border border-border bg-background text-sm rounded-none focus:ring-2 focus:ring-blue-800">
            {months.map((m) => <option key={m.month} value={m.month}>{m.label}</option>)}
          </select>
          {month && (
            <a href={`${API}/dashboard/payouts/statement.pdf?month=${month}`} data-testid="statement-pdf-button"
              className="inline-flex items-center h-10 px-4 bg-blue-900 hover:bg-blue-800 text-white uppercase tracking-widest text-xs transition-colors duration-200">
              <Download className="h-3.5 w-3.5 mr-2" /> PDF
            </a>
          )}
        </div>
      </div>

      {!stmt ? (
        <div className="h-24 border border-border bg-muted animate-pulse mt-6" />
      ) : (
        <>
          <div className="mt-6 flex items-baseline gap-3">
            <p className="label-caps">Received in {stmt.label}</p>
            <p className="font-display text-2xl" data-testid="statement-total">{money(stmt.total)}</p>
          </div>
          {stmt.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground mt-4" data-testid="statement-empty">No fee notes were settled in this month.</p>
          ) : (
            <div className="mt-4 border border-border overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    {["Paid on", "Fee note", "Reference", "Note amount", "Your share"].map((h) => (
                      <th key={h} className="p-3 text-left label-caps whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody data-testid="statement-rows">
                  {stmt.rows.map((r) => (
                    <tr key={r.invoice_id} className="border-b border-border last:border-0" data-testid={`statement-row-${r.invoice_id}`}>
                      <td className="p-3 whitespace-nowrap">{fmtDate(r.paid_at)}</td>
                      <td className="p-3 font-mono text-xs whitespace-nowrap">{r.invoice_number}</td>
                      <td className="p-3">
                        <span className="font-mono text-xs">{r.case_number || r.case_ref}</span>
                        <span className="block text-xs text-muted-foreground mt-0.5">{r.case_title}</span>
                      </td>
                      <td className="p-3 font-mono whitespace-nowrap text-muted-foreground">{money(r.note_amount)}</td>
                      <td className="p-3 font-mono whitespace-nowrap">{money(r.share)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
};
